/**
 * Lightweight, safe Markdown-to-HTML parser for ChatBot responses.
 * Converts markdown headers, bold/italic, lists (ordered and unordered),
 * horizontal rules, and links into clean HTML for display.
 */

function escapeHtml(str) {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function formatChatMarkdown(text) {
  if (!text || typeof text !== "string") return "";

  const lines = text.split("\n");
  const output = [];
  let inUl = false;
  let inOl = false;
  let inSubUl = false;

  const closeSubUl = () => {
    if (inSubUl) {
      output.push("</ul>");
      inSubUl = false;
    }
  };

  const closeLists = () => {
    closeSubUl();
    if (inUl) {
      output.push("</ul>");
      inUl = false;
    }
    if (inOl) {
      output.push("</ol>");
      inOl = false;
    }
  };

  const formatInline = (str) => {
    let s = escapeHtml(str);

    // Bold + Italic: ***text*** or ___text___
    s = s.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");

    // Bold: **text** or __text__
    s = s.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/__(.*?)__/g, "<strong>$1</strong>");

    // Italic: *text* (ensure not immediately followed or preceded by space or asterisk)
    s = s.replace(/(^|[^\*])\*(?!\s)([^*]+?)\*(?!\*)/g, "$1<em>$2</em>");

    // Markdown Links: [text](url)
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
      const cleanUrl = url.replace(/&amp;/g, "&");
      return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
    });

    // Inline code: `code`
    s = s.replace(/`([^`]+)`/g, "<code>$1</code>");

    return s;
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    // Empty line
    if (!trimmed) {
      closeLists();
      continue;
    }

    // Horizontal Rule: --- or *** or ___
    if (/^(---|___|\*\*\*)$/.test(trimmed)) {
      closeLists();
      output.push('<hr class="chat-hr" />');
      continue;
    }

    // Heading 3: ### Heading
    if (/^###\s+(.*)/.test(trimmed)) {
      closeLists();
      const title = trimmed.replace(/^###\s+/, "");
      output.push(`<h5 class="chat-heading-3">${formatInline(title)}</h5>`);
      continue;
    }

    // Heading 2: ## Heading
    if (/^##\s+(.*)/.test(trimmed)) {
      closeLists();
      const title = trimmed.replace(/^##\s+/, "");
      output.push(`<h4 class="chat-heading-2">${formatInline(title)}</h4>`);
      continue;
    }

    // Heading 1: # Heading
    if (/^#\s+(.*)/.test(trimmed)) {
      closeLists();
      const title = trimmed.replace(/^#\s+/, "");
      output.push(`<h3 class="chat-heading-1">${formatInline(title)}</h3>`);
      continue;
    }

    // Sub-item (indented bullet under ol or ul): e.g. "   - Upload..."
    const subMatch = rawLine.match(/^(\s{2,}|\t+)(?:-|\*)\s+(.*)/);
    if (subMatch && (inOl || inUl)) {
      if (!inSubUl) {
        output.push('<ul class="chat-sub-ul">');
        inSubUl = true;
      }
      output.push(`<li>${formatInline(subMatch[2])}</li>`);
      continue;
    }

    // Normal unordered list item: - Item or * Item
    const ulMatch = trimmed.match(/^(?:-|\*)\s+(.*)/);
    if (ulMatch) {
      closeSubUl();
      if (inOl) {
        output.push("</ol>");
        inOl = false;
      }
      if (!inUl) {
        output.push('<ul class="chat-ul">');
        inUl = true;
      }
      output.push(`<li>${formatInline(ulMatch[1])}</li>`);
      continue;
    }

    // Ordered list item: 1. Item
    const olMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
    if (olMatch) {
      closeSubUl();
      if (inUl) {
        output.push("</ul>");
        inUl = false;
      }
      if (!inOl) {
        output.push('<ol class="chat-ol">');
        inOl = true;
      }
      output.push(`<li>${formatInline(olMatch[2])}</li>`);
      continue;
    }

    // Normal paragraph line
    closeLists();
    output.push(`<p class="chat-p">${formatInline(trimmed)}</p>`);
  }

  closeLists();
  return output.join("");
}
